import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { Category, ContractDetail, DataConnectRow, PlanRow } from '@/lib/api/credit-contracts';
import { contains, formatPeriod } from '@/lib/credit-contracts/month';

import { categoryLabels, formatUpdatedAt, numberText, planLabels, yenEquivalent } from './display';
import { compactTableSx, fitColumnSx, HeaderLabel, stickyCell, stickyRowSx } from './tableParts';

const numberCell = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } as const;
// 数値の列は同じ幅にそろえる
const numberColumnSx = { ...numberCell, ...fitColumnSx(160) };

const categoryColors: Record<Category, 'info' | 'success' | 'default'> = {
  scheduled: 'info',
  active: 'success',
  past: 'default',
};

// 横スクロールしても動かない列（左の期間と、右の操作）
const PERIOD_WIDTH = 200;
// 操作の列：鉛筆とごみ箱の決まった置き場所（24px ずつ、間 8px）と、左右の余白 16px
const ACTIONS_WIDTH = 24 + 8 + 24 + 32;
const periodCol = (header?: boolean) => stickyCell('left', 0, PERIOD_WIDTH, { header, edge: true });
const actionsCol = (header?: boolean) =>
  stickyCell('right', 0, ACTIONS_WIDTH, { header, edge: true });

/**
 * タブの中身。注意書きを上に置いたまま、表の見出しの行を固定し、値の行だけをスクロールする。
 * 表は行の分の高さをとり、親（高さの決まった縦並びの flex）に収まらないときは、値の行をスクロールする。
 * 列は中身の幅に合わせ、余った幅は右端の空の列が受け取る。幅が足りないときは、期間と操作の列を固定して横にスクロールする
 */
function TabBody({ note, children }: { note?: string; children: React.ReactNode }) {
  return (
    <>
      {note && (
        <Typography variant="body2" color="text.secondary" px={2} py={1} flexShrink={0}>
          {note}
        </Typography>
      )}
      <TableContainer sx={{ flex: '0 1 auto', minHeight: 0, overflow: 'auto' }}>
        {children}
      </TableContainer>
    </>
  );
}

/** 開いているタブの一覧に行を追加する */
export function AddButton({ disabled, onClick }: { disabled: boolean; onClick: () => void }) {
  return (
    <Button size="sm" onClick={onClick} disabled={disabled}>
      <PlusIcon />
      追加
    </Button>
  );
}

function EmptyRow({ colSpan }: { colSpan: number }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan}>
        <Typography variant="body2" color="text.secondary" py={1}>
          行はありません。
        </Typography>
      </TableCell>
    </TableRow>
  );
}

/** 更新日時と更新者の列の見出し */
function UpdatedHeaders() {
  return (
    <>
      <TableCell sx={fitColumnSx(170)}>更新日時</TableCell>
      <TableCell sx={fitColumnSx(260)}>更新者</TableCell>
    </>
  );
}

/** 更新日時と更新者の列 */
function UpdatedCells({ row }: { row: PlanRow | DataConnectRow }) {
  return (
    <>
      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatUpdatedAt(row.updated_at)}
      </TableCell>
      <TableCell>{row.updated_by}</TableCell>
    </>
  );
}

/** 契約料金プランの一覧（開始月の新しい順）。どの行も編集・削除できる */
export function PlanTable({
  detail,
  disabled,
  onEdit,
  onDelete,
}: {
  detail: ContractDetail;
  disabled: boolean;
  onEdit: (row: PlanRow) => void;
  onDelete: (row: PlanRow) => void;
}) {
  return (
    <TabBody>
      <Table stickyHeader size="small" aria-label="契約料金プランの一覧" sx={compactTableSx}>
        <TableHead>
          <TableRow>
            <TableCell {...periodCol(true)}>期間</TableCell>
            <TableCell sx={fitColumnSx(130)}>料金プラン</TableCell>
            <TableCell sx={numberColumnSx}>
              <HeaderLabel name="契約クレジット数" unit="（クレジット/月）" />
            </TableCell>
            <TableCell sx={numberColumnSx}>
              <HeaderLabel name="クレジット販売単価" unit="（円/クレジット）" />
            </TableCell>
            <TableCell sx={numberColumnSx}>
              <HeaderLabel name="月額料金" unit="（円）" />
            </TableCell>
            <UpdatedHeaders />
            <TableCell {...actionsCol(true)} aria-label="操作" />
            <TableCell aria-hidden />
          </TableRow>
        </TableHead>
        <TableBody>
          {detail.pricing_plans.length === 0 && <EmptyRow colSpan={9} />}
          {detail.pricing_plans.map((row) => (
            <TableRow key={row.start_month} sx={stickyRowSx}>
              <TableCell {...periodCol()}>
                {formatPeriod(row)}
                {contains(row, detail.current_month) && (
                  <Chip size="small" color="success" label="当月" sx={{ ml: 1, height: 20 }} />
                )}
              </TableCell>
              <TableCell>{planLabels[row.pricing_plan]}</TableCell>
              <TableCell sx={numberCell}>{numberText(row.monthly_contract_credit)}</TableCell>
              <TableCell sx={numberCell}>{numberText(row.credit_unit_price)}</TableCell>
              <TableCell sx={numberCell}>{numberText(row.monthly_fee)}</TableCell>
              <UpdatedCells row={row} />
              <TableCell {...actionsCol()}>
                <RowActions
                  label={formatPeriod(row)}
                  disabled={disabled}
                  onEdit={() => onEdit(row)}
                  onDelete={() => onDelete(row)}
                />
              </TableCell>
              <TableCell aria-hidden />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TabBody>
  );
}

/**
 * データコネクトのクレジット数の一覧（開始月の新しい順）。
 * 予定の行は編集・削除、適用中の行は終了月の変更だけ、過去の行は変えられない
 */
export function DataConnectTable({
  detail,
  disabled,
  onEdit,
  onDelete,
}: {
  detail: ContractDetail;
  disabled: boolean;
  onEdit: (row: DataConnectRow) => void;
  onDelete: (row: DataConnectRow) => void;
}) {
  return (
    <TabBody note="当月以前の月の値は変えられません。誤りは、クレジット制の運用の担当者に連絡してください。">
      <Table
        stickyHeader
        size="small"
        aria-label="データコネクトのクレジット数の一覧"
        sx={compactTableSx}
      >
        <TableHead>
          <TableRow>
            <TableCell {...periodCol(true)}>期間</TableCell>
            <TableCell sx={numberColumnSx}>
              <HeaderLabel name="クレジット数" unit="（クレジット/月）" />
            </TableCell>
            <TableCell sx={numberColumnSx}>
              <HeaderLabel name="相当額" unit="（円/月）" />
            </TableCell>
            <TableCell sx={fitColumnSx(100)}>区分</TableCell>
            <UpdatedHeaders />
            <TableCell {...actionsCol(true)} aria-label="操作" />
            <TableCell aria-hidden />
          </TableRow>
        </TableHead>
        <TableBody>
          {detail.data_connect_credits.length === 0 && <EmptyRow colSpan={8} />}
          {detail.data_connect_credits.map((row) => (
            <TableRow key={row.start_month} sx={stickyRowSx}>
              <TableCell {...periodCol()}>{formatPeriod(row)}</TableCell>
              <TableCell sx={numberCell}>{numberText(row.monthly_credit)}</TableCell>
              <TableCell sx={numberCell}>
                <YenEquivalent
                  value={yenEquivalent(
                    row.monthly_credit,
                    row.start_month,
                    detail.credit_face_values,
                  )}
                />
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={categoryLabels[row.category]}
                  color={categoryColors[row.category]}
                  sx={{ height: 20 }}
                />
              </TableCell>
              <UpdatedCells row={row} />
              <TableCell {...actionsCol()}>
                {row.category === 'scheduled' && (
                  <RowActions
                    label={formatPeriod(row)}
                    disabled={disabled}
                    onEdit={() => onEdit(row)}
                    onDelete={() => onDelete(row)}
                  />
                )}
                {row.category === 'past' && <ActionSlots />}
                {row.category === 'active' && (
                  <ActionSlots
                    edit={
                      <IconAction
                        title="終了月を変更"
                        label="終了月を変更"
                        disabled={disabled}
                        onClick={() => onEdit(row)}
                      >
                        <PencilIcon />
                      </IconAction>
                    }
                  />
                )}
              </TableCell>
              <TableCell aria-hidden />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TabBody>
  );
}

/** 相当額。開始月の1日に有効な額面がないときは、その旨を示す */
function YenEquivalent({ value }: { value: string | null }) {
  if (value !== null) return <>{value}</>;
  return (
    <Typography
      component="span"
      variant="body2"
      color="text.secondary"
      title="開始月の1日に有効な1クレジットの額面がありません"
    >
      額面なし
    </Typography>
  );
}

/** 行の操作のアイコンのボタン。名前はマウスを載せると示す */
function IconAction({
  title,
  label,
  disabled,
  destructive,
  onClick,
  children,
}: {
  title: string;
  label: string;
  disabled: boolean;
  destructive?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip title={title}>
      {/* 押せないボタンでも名前を示せるよう、span で包む */}
      <span className="inline-flex">
        <Button
          size="sm"
          variant="outline"
          className={`h-6 w-6 p-0 ${destructive ? 'text-red-600 hover:text-red-700' : ''}`}
          disabled={disabled}
          onClick={onClick}
          aria-label={label}
        >
          {children}
        </Button>
      </span>
    </Tooltip>
  );
}

function RowActions({
  label,
  disabled,
  onEdit,
  onDelete,
}: {
  label: string;
  disabled: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <ActionSlots
      edit={
        <IconAction title="編集" label={`${label}の行を編集`} disabled={disabled} onClick={onEdit}>
          <PencilIcon />
        </IconAction>
      }
      remove={
        <IconAction
          title="削除"
          label={`${label}の行を削除`}
          disabled={disabled}
          destructive
          onClick={onDelete}
        >
          <Trash2Icon />
        </IconAction>
      }
    />
  );
}

/**
 * 操作のボタンの決まった置き場所（左が鉛筆、右がごみ箱）。
 * 鉛筆だけの行もごみ箱の場所を空けておき、どの行でも同じアイコンを同じ位置に並べる。
 * ボタンのない行も同じ高さをとり、行の高さをそろえる
 */
function ActionSlots({ edit, remove }: { edit?: React.ReactNode; remove?: React.ReactNode }) {
  return (
    <Box display="grid" gridTemplateColumns="24px 24px" columnGap={1} minHeight={24}>
      <Box display="flex">{edit}</Box>
      <Box display="flex">{remove}</Box>
    </Box>
  );
}
